import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, shareReplay, take } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../../environments/environment';
import { SnackBarService } from './snackbar.service';

export type CustomerType = 'shipper' | 'broker' | 'agent';

export interface VSTUserInfo {
  userId: string;
  userName: string;
  userEmail: string;
  internal: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // @ts-ignore
  private userInfo$$ = new BehaviorSubject<VSTUserInfo>(null);
  public userInfo$ = this.userInfo$$.asObservable().pipe(shareReplay(1));
  private httpClientNoInterceptors: HttpClient;
  // @ts-ignore
  private isLoggedIn$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  public isLoggedIn$ = this.isLoggedIn$$.asObservable().pipe(
    filter((isLoggedIn) => isLoggedIn !== null),
    distinctUntilChanged(),
    shareReplay(1),
  );

  constructor(
    httpBackend: HttpBackend,
    private logger: NGXLogger,
    private afAuth: AngularFireAuth,
    private snackbar: SnackBarService,
  ) {
    this.httpClientNoInterceptors = new HttpClient(httpBackend);

    this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        await this.loadUserInfo();
        const userInfo = await lastValueFrom(this.userInfo$.pipe(take(1)));
        if (!userInfo) {
          this.isLoggedIn$$.next(false);
          this.logout();
          return;
        }
        this.isLoggedIn$$.next(true);
      } else {
        this.isLoggedIn$$.next(false);
      }
    });
  }

  public async loginWithEmail(email: string, password: string): Promise<boolean> {
    try {
      const result = await lastValueFrom(
        this.httpClientNoInterceptors.post<{ customToken: string }>(
          `${environment.api}/v1/login/password_email`,
          { email, password },
          {
            headers: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'Content-Type': 'application/json',
              'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
              'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
            },
          },
        ),
      );
      await this.afAuth.signInWithCustomToken(result.customToken);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async logout(redirect = true) {
    await this.afAuth.signOut();
    if (redirect) {
      window.location.pathname = '/';
    }
  }

  public async loadUserInfo() {
    const token = await lastValueFrom(
      this.afAuth.idToken.pipe(
        filter((t) => !!t),
        take(1),
      ),
    );
    try {
      const userInfo = await lastValueFrom(
        this.httpClientNoInterceptors.get<VSTUserInfo>(`${environment.api}/v2/vst/user_info`, {
          headers: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: `Bearer ${token}`,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
            'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
          },
        }),
      );
      this.userInfo$$.next(userInfo);
    } catch (e) {
      this.snackbar.showError('Invalid User');
      await this.afAuth.signOut();
    }
  }

  public async doPasswordReset(resetCode: string, newPassword: string): Promise<string> {
    try {
      const result = await lastValueFrom(
        this.httpClientNoInterceptors.post<{ message: string }>(
          `${environment.api}/v1/do_password_reset`,
          {
            resetCode,
            newPassword,
          },
          {
            headers: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      return result.message;
    } catch (error) {
      this.logger.error(error);
      return null;
    }
  }

  public async getPasswordResetCode(email: string): Promise<string> {
    try {
      const result = await lastValueFrom(
        await this.httpClientNoInterceptors.post<{ message: string }>(
          `${environment.api}/v1/password_reset_code_email`,
          {
            email,
          },
          {
            headers: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'Content-Type': 'application/json',
            },
          },
        ),
      );
      return result.message;
    } catch (error) {
      this.logger.error(error);
      return null;
    }
  }

  public async loginWithCustomToken(token: string): Promise<boolean> {
    try {
      await this.afAuth.signInWithCustomToken(token);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async loginWithLoHiToken(token: string): Promise<boolean> {
    try {
      const result = await lastValueFrom(
        this.httpClientNoInterceptors.get<{ customToken: string }>(`${environment.api}/v1/login/lohi_token`, {
          headers: {
            'Content-Type': 'application/json',
            'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
          },
          params: {
            token,
          },
        }),
      );
      await this.afAuth.signInWithCustomToken(result.customToken);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }
}
